import { useEffect } from 'react'

type KeyBindingHandlers = {
  delete?: () => void
  enter?: (e: KeyboardEvent) => void
  toggleGroup?: () => void // Ctrl+G toggles between group/ungroup
  selectAll?: () => void
  // Arrow-key nudge: dx/dy in canvas pixels (1px, or 10px with Shift).
  nudge?: (dx: number, dy: number) => void
  copy?: () => void // Cmd/Ctrl+C
  paste?: () => void // Cmd/Ctrl+V
  undo?: () => void // Cmd/Ctrl+Z
  redo?: () => void // Cmd/Ctrl+Shift+Z or Cmd/Ctrl+Y
}

// Arrow keys mapped to a unit direction; magnitude is applied by the caller.
const NUDGE_DIRECTIONS: Record<string, readonly [number, number]> = {
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
}

export function useKeyBindings(isActive: boolean, handlers: KeyBindingHandlers) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActive) return

      // When focus is inside an editable surface (input, textarea, or any
      // contenteditable like the Lexical canvas text editor), the user's
      // intent for Cmd+A / Cmd+G / Delete / Backspace is the field, not the
      // canvas. The window-level listener still fires there because we're
      // bound to window, so gate canvas shortcuts on the target. Cmd+Enter
      // is intentionally left alone — it's a submit affordance for inputs.
      const inEditableSurface = isEditableTarget(e.target)

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'g') {
        if (inEditableSurface) return
        e.preventDefault()
        handlers.toggleGroup?.()
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        if (inEditableSurface) return
        e.preventDefault()
        handlers.selectAll?.()
        return
      }

      // Undo / redo. Cmd/Ctrl+Z (Shift for redo), plus Cmd/Ctrl+Y for redo.
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        if (inEditableSurface) return
        e.preventDefault()
        if (e.shiftKey) handlers.redo?.()
        else handlers.undo?.()
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        if (inEditableSurface) return
        e.preventDefault()
        handlers.redo?.()
        return
      }

      // Copy / paste of canvas nodes. No preventDefault so a real text selection
      // outside inputs still copies natively; our node clipboard is in-memory and
      // separate from the OS clipboard.
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        if (inEditableSurface) return
        handlers.copy?.()
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        if (inEditableSurface) return
        handlers.paste?.()
        return
      }

      const nudgeDirection = NUDGE_DIRECTIONS[e.key]
      if (nudgeDirection && handlers.nudge) {
        // Let inputs and browser/modifier shortcuts (Cmd+Arrow, etc.) win.
        if (inEditableSurface || e.metaKey || e.ctrlKey || e.altKey) return
        e.preventDefault()
        const step = e.shiftKey ? 10 : 1
        handlers.nudge(nudgeDirection[0] * step, nudgeDirection[1] * step)
        return
      }

      switch (e.key) {
        case 'Delete':
          if (inEditableSurface) return
          e.preventDefault()
          handlers.delete?.()
          break

        case 'Backspace':
          if (inEditableSurface) return
          e.preventDefault()
          handlers.delete?.()
          break

        case 'Enter':
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault()
            handlers.enter?.(e)
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isActive, handlers])
}

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return true
  return target.isContentEditable
}
